import { useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * 阶段工时估算结果
 */
export interface StageEstimate {
  id: string
  stage: string
  estimatedHours: number
  actualHours: number | null
  variance: number | null
  timeStatus: 'on_track' | 'approaching_deadline' | 'overdue' | null
}

export interface TimeComparisonChartProps {
  /** 阶段估算数据 */
  estimates: StageEstimate[]
  /** 是否显示所有阶段（包括未完成的） */
  showAll?: boolean
}

/**
 * 工时对比图表组件
 * 
 * 使用 CSS 实现简单条形图，展示估算工时与实际工时的对比：
 * - 偏差着色逻辑：超出 20% 红色，节省 20% 绿色，其他蓝色
 * - 悬停显示详细数字
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.4
 */
export function TimeComparisonChart({
  estimates,
  showAll = false,
}: TimeComparisonChartProps) {
  // 过滤有实际工时的阶段
  const completedEstimates = showAll
    ? estimates
    : estimates.filter((e) => e.actualHours !== null)

  if (completedEstimates.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        暂无已完成阶段的工时数据
      </div>
    )
  }

  // 计算最大值用于缩放
  const maxHours = Math.max(
    ...completedEstimates.flatMap((e) => [
      e.estimatedHours,
      e.actualHours ?? 0,
    ])
  )

  return (
    <div className="space-y-3">
      {completedEstimates.map((estimate) => (
        <ComparisonBar
          key={estimate.id}
          estimate={estimate}
          maxHours={maxHours}
        />
      ))}

      {/* 图例 */}
      <div className="flex items-center gap-4 pt-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="h-2 w-4 rounded bg-blue-500/30" />
          <span>预估</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-4 rounded bg-blue-500" />
          <span>实际</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-4 rounded bg-green-500" />
          <span>节省 &gt;20%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-4 rounded bg-red-500" />
          <span>超出 &gt;20%</span>
        </div>
      </div>
    </div>
  )
}

/**
 * 单个对比条
 */
interface ComparisonBarProps {
  estimate: StageEstimate
  maxHours: number
}

function ComparisonBar({ estimate, maxHours }: ComparisonBarProps) {
  const [isHovered, setIsHovered] = useState(false)

  const estimatedWidth = maxHours > 0 ? (estimate.estimatedHours / maxHours) * 100 : 0
  const actualWidth =
    estimate.actualHours !== null && maxHours > 0
      ? (estimate.actualHours / maxHours) * 100
      : 0

  // 计算偏差颜色 (Requirement 9.2, 9.3)
  const varianceColor = getVarianceColor(
    estimate.estimatedHours,
    estimate.actualHours
  )

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 阶段名称 */}
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium">{getStageLabel(estimate.stage)}</span>
        {estimate.variance !== null && (
          <span className={cn('text-xs font-medium', varianceColor.text)}>
            {estimate.variance > 0 ? '+' : ''}
            {estimate.variance.toFixed(1)}h
          </span>
        )}
      </div>

      {/* 条形图容器 */}
      <div className="space-y-1">
        {/* 预估条 */}
        <div className="relative h-4 w-full rounded bg-muted">
          <div
            className="h-full rounded bg-blue-500/30 transition-all duration-300"
            style={{ width: `${estimatedWidth}%` }}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            预估: {estimate.estimatedHours.toFixed(1)}h
          </span>
        </div>

        {/* 实际条 */}
        {estimate.actualHours !== null && (
          <div className="relative h-4 w-full rounded bg-muted">
            <div
              className={cn(
                'h-full rounded transition-all duration-300',
                varianceColor.bar
              )}
              style={{ width: `${actualWidth}%` }}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              实际: {estimate.actualHours.toFixed(1)}h
            </span>
          </div>
        )}
      </div>

      {/* 悬停详情 (Requirement 9.4) */}
      {isHovered && estimate.actualHours !== null && (
        <div className="absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 rounded-md border bg-popover p-2 shadow-md">
          <div className="whitespace-nowrap text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">预估工时:</span>
              <span className="font-medium">{estimate.estimatedHours.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">实际工时:</span>
              <span className="font-medium">{estimate.actualHours.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between gap-4 border-t pt-1 mt-1">
              <span className="text-muted-foreground">偏差:</span>
              <span className={cn('font-medium', varianceColor.text)}>
                {estimate.variance !== null
                  ? `${estimate.variance > 0 ? '+' : ''}${estimate.variance.toFixed(1)}h`
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">偏差率:</span>
              <span className={cn('font-medium', varianceColor.text)}>
                {getVariancePercentage(estimate.estimatedHours, estimate.actualHours)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 获取偏差颜色
 * Property 5: Time Variance Coloring
 * - 超出 20%+ 红色
 * - 节省 20%+ 绿色
 * - 其他蓝色/灰色
 * 
 * Requirements: 9.2, 9.3
 */
export function getVarianceColor(
  estimated: number,
  actual: number | null
): { bar: string; text: string } {
  if (actual === null || estimated === 0) {
    return {
      bar: 'bg-blue-500',
      text: 'text-muted-foreground',
    }
  }

  const ratio = actual / estimated

  // 超出 20%+ (actual > estimated * 1.2)
  if (ratio > 1.2) {
    return {
      bar: 'bg-red-500',
      text: 'text-red-600 dark:text-red-400',
    }
  }

  // 节省 20%+ (actual < estimated * 0.8)
  if (ratio < 0.8) {
    return {
      bar: 'bg-green-500',
      text: 'text-green-600 dark:text-green-400',
    }
  }

  // 正常范围
  return {
    bar: 'bg-blue-500',
    text: 'text-muted-foreground',
  }
}

/**
 * 计算偏差百分比
 */
function getVariancePercentage(estimated: number, actual: number | null): string {
  if (actual === null || estimated === 0) {return '-'}
  const percentage = ((actual - estimated) / estimated) * 100
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(0)}%`
}

/**
 * 获取阶段标签
 */
function getStageLabel(stage: string): string {
  const labels: Record<string, string> = {
    created: '已创建',
    product_discussion: '产品讨论',
    ui_design: 'UI 设计',
    ui_confirmation: 'UI 确认',
    frontend_development: '前端开发',
    backend_development: '后端开发',
    code_review: '代码审查',
    testing: '测试',
    product_acceptance: '产品验收',
    done: '已完成',
  }
  return labels[stage] ?? stage
}

/**
 * 计算偏差类型
 * 用于属性测试
 */
export type VarianceType = 'over' | 'under' | 'normal'

export function calculateVarianceType(
  estimated: number,
  actual: number | null
): VarianceType {
  if (actual === null || estimated === 0) {return 'normal'}
  
  const ratio = actual / estimated
  
  if (ratio > 1.2) {return 'over'}
  if (ratio < 0.8) {return 'under'}
  return 'normal'
}

/**
 * 验证偏差着色逻辑
 * 用于属性测试
 * 
 * Property 5: Time Variance Coloring
 * Validates: Requirements 9.2, 9.3
 */
export function validateVarianceColoring(
  estimated: number,
  actual: number | null
): {
  varianceType: VarianceType
  expectedBarColor: 'red' | 'green' | 'blue'
  isCorrect: boolean
} {
  const varianceType = calculateVarianceType(estimated, actual)
  const color = getVarianceColor(estimated, actual)

  let expectedBarColor: 'red' | 'green' | 'blue'
  switch (varianceType) {
    case 'over':
      expectedBarColor = 'red'
      break
    case 'under':
      expectedBarColor = 'green'
      break
    default:
      expectedBarColor = 'blue'
  }

  const isCorrect =
    (varianceType === 'over' && color.bar.includes('red')) ||
    (varianceType === 'under' && color.bar.includes('green')) ||
    (varianceType === 'normal' && color.bar.includes('blue'))

  return {
    varianceType,
    expectedBarColor,
    isCorrect,
  }
}
